class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.allPredictiveSearchInstances = document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";
    this.showSuggestions = this.getAttribute("data-show-suggestions") === "true";

    // Get wrapper elements
    this.defaultWrapper = this.querySelector("#predictive-search-default-wrapper");
    this.resultsWrapper = this.querySelector("#predictive-search-results-wrapper");

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));

    // Handle resize for mobile
    let resizeTimeout;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (this.isOpen && window.innerWidth < 750) {
          this.open(); // Recalculate
        }
      }, 150);
    });

    this.setupCloseResetButton();
  }

  setupCloseResetButton() {
    const closeBtn = this.closest(".search-modal")?.querySelector("[data-search-close-reset]");
    if (!closeBtn) return;

    closeBtn.addEventListener("click", (e) => {
      if (this.input && this.input.value.trim() !== "") {
        e.preventDefault();
        e.stopPropagation();
        this.input.value = "";
        this.searchTerm = "";

        if (this.showSuggestions) {
          this.showDefaultSuggestions();
        } else {
          this.closeResults(true);
        }

        this.input.focus();
      }
    });
  }
  
  getQuery() {
    return this.input.value.trim();
  }

  showDefaultSuggestions() {
    if (this.defaultWrapper) {
      this.defaultWrapper.style.display = "block";
    }
    if (this.resultsWrapper) {
      this.resultsWrapper.style.display = "none";
    }

    this.setAttribute("results", true);
    this.open();
    this.initDefaultCarousel();
  }

  hideDefaultSuggestions() {
    if (this.defaultWrapper) {
      this.defaultWrapper.style.display = "none";
    }
    if (this.resultsWrapper) {
      this.resultsWrapper.style.display = "block";
    }
  }

  initDefaultCarousel() {
    const carouselElement = this.defaultWrapper?.querySelector("#predictive-search-carousel-default");

    if (!carouselElement || carouselElement.classList.contains("is-initialized")) {
      return;
    }

    requestAnimationFrame(() => {
      if (typeof Splide !== "undefined") {
        const splide = new Splide(carouselElement, {
          type: "slide",
          perPage: 5,
          perMove: 1,
          gap: "1.6rem",
          pagination: false,
          arrows: false,
          breakpoints: {
            1400: { perPage: 4 },
            990: { perPage: 2 },
          },
        });

        // Update fade gradients on move
        const updateFadeGradients = () => {
          const leftFade = carouselElement.parentElement.querySelector(".splide-fade-left");
          const rightFade = carouselElement.parentElement.querySelector(".splide-fade-right");

          if (!leftFade || !rightFade) return;

          const index = splide.index;
          const lastIndex = splide.Components.Controller.getEnd();

          // Show left fade if not at start
          if (index > 0) {
            leftFade.classList.remove("tw-opacity-0");
            leftFade.classList.add("tw-opacity-100");
          } else {
            leftFade.classList.remove("tw-opacity-100");
            leftFade.classList.add("tw-opacity-0");
          }

          // Show right fade if not at end
          if (index < lastIndex) {
            rightFade.classList.remove("tw-opacity-0");
            rightFade.classList.add("tw-opacity-100");
          } else {
            rightFade.classList.remove("tw-opacity-100");
            rightFade.classList.add("tw-opacity-0");
          }
        };

        splide.on("mounted", updateFadeGradients);
        splide.on("moved", updateFadeGradients);
        splide.on("resize", updateFadeGradients);

        splide.mount();
        carouselElement.classList.add("is-initialized");

        console.log("Default carousel with fade gradients mounted");
      }
    });
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      if (this.showSuggestions) {
        this.showDefaultSuggestions();
      } else {
        this.close(true);
      }
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();

      if (this.showSuggestions) {
        this.showDefaultSuggestions();
      } else {
        this.closeResults(true);
      }
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      if (this.showSuggestions) {
        this.showDefaultSuggestions();
      }
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      if (this.showSuggestions) {
        this.showDefaultSuggestions();
      } else {
        this.close(true);
      }
      event.preventDefault();
      return;
    }
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector("[data-predictive-search-search-for-text]");
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (previousTerm && currentButtonText.match(new RegExp(previousTerm, "g"))?.length > 1) {
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(this.querySelectorAll("li, button.predictive-search__item")).filter(
      (element) => element.offsetParent !== null,
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();

    if (this.resultsWrapper) {
      this.resultsWrapper.innerHTML = "";
    }

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const searchParams = new URLSearchParams({
      q: searchTerm,
      section_id: "predictive-search",
      "resources[type]": "product,query",
      "resources[limit]": "8",
      "resources[limit_scope]": "each",
      "resources[options][unavailable_products]": "last",
      "resources[options][fields]": "title,product_type,variants.title,vendor",
    });

    fetch(`${routes.predictive_search_url}?${searchParams.toString()}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.removeAttribute("loading");

    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");
    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.hideDefaultSuggestions();

    if (this.resultsWrapper) {
      this.resultsWrapper.innerHTML = resultsMarkup;
    }

    this.setAttribute("results", true);
    this.setLiveRegionResults();
    this.open();
    this.initProductCarousel();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(this.querySelector("[data-predictive-search-live-region-count-value]").textContent);
  }

  getResultsMaxHeight() {
    const isMobile = window.innerWidth < 750;

    if (isMobile) {
      const header = document.querySelector(".section-header");
      const headerHeight = header ? header.offsetHeight : 0;

      const searchForm = this.querySelector(".search");
      const searchFormHeight = searchForm ? searchForm.offsetHeight : 0;

      this.resultsMaxHeight = window.innerHeight - headerHeight - searchFormHeight - 20;
    } else {
      const headerBottom = document.querySelector(".section-header")?.getBoundingClientRect().bottom || 0;
      this.resultsMaxHeight = window.innerHeight - headerBottom - 30;
    }

    return this.resultsMaxHeight;
  }

  open() {
    const isMobile = window.innerWidth < 750;

    if (isMobile) {
      const header = document.querySelector(".section-header");
      const headerBottom = header ? header.getBoundingClientRect().bottom : 0;

      const availableHeight = window.innerHeight - headerBottom - 20;

      this.predictiveSearchResults.style.maxHeight = `${availableHeight}px`;
      this.predictiveSearchResults.style.height = "auto";
      this.predictiveSearchResults.style.overflowY = "auto";
      this.predictiveSearchResults.style.overflowX = "hidden";
      this.predictiveSearchResults.style.webkitOverflowScrolling = "touch";
    } else {
      // Desktop
      const maxHeight = this.resultsMaxHeight || this.getResultsMaxHeight();
      this.predictiveSearchResults.style.overflowY = "auto";
      this.predictiveSearchResults.style.overflowX = "hidden";
    }

    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (this.showSuggestions && !clearSearchTerm) {
      return;
    }
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");
  }

  initProductCarousel() {
    setTimeout(() => {
      const carouselElement = this.resultsWrapper.querySelector("#predictive-search-carousel");

      if (!carouselElement || typeof Splide === "undefined") {
        return;
      }

      const splide = new Splide("#predictive-search-carousel", {
        type: "slide",
        perPage: 5,
        perMove: 1,
        gap: "1.6rem",
        padding: 0,
        pagination: false,
        arrows: false,
        breakpoints: {
          1400: {
            perPage: 4,
          },
          990: {
            perPage: 2,
          },
        },
      });

      // Update fade gradients on move
      const updateFadeGradients = () => {
        const leftFade = carouselElement.parentElement.querySelector(".splide-fade-left");
        const rightFade = carouselElement.parentElement.querySelector(".splide-fade-right");

        if (!leftFade || !rightFade) return;

        const index = splide.index;
        const lastIndex = splide.Components.Controller.getEnd();

        // Show left fade if not at start
        if (index > 0) {
          leftFade.classList.remove("tw-opacity-0");
          leftFade.classList.add("tw-opacity-100");
        } else {
          leftFade.classList.remove("tw-opacity-100");
          leftFade.classList.add("tw-opacity-0");
        }

        // Show right fade if not at end
        if (index < lastIndex) {
          rightFade.classList.remove("tw-opacity-0");
          rightFade.classList.add("tw-opacity-100");
        } else {
          rightFade.classList.remove("tw-opacity-100");
          rightFade.classList.add("tw-opacity-0");
        }
      };

      splide.on("mounted", updateFadeGradients);
      splide.on("moved", updateFadeGradients);
      splide.on("resize", updateFadeGradients);

      splide.mount();

      console.log("Product carousel with fade gradients mounted");
    }, 100);
  }
}

customElements.define("predictive-search", PredictiveSearch);